import { defaultTo, groupBy, intersection, isNil, last, values } from 'ramda';
import { ApiResponse } from '../@types/ApiResponse';
import { OrgUser } from '../@types/ApiResponse/OrgUser';
import { OrgGroups } from '../@types/ApiResponse/OrgGroups';

/**
 * 返回 false 说明不在分组内，
 * 返回 true 说明在分组内
 */
export const checkGroup = (group: string[], userInfo: ApiResponse.OrgUser.OrgUserItem) => {
  // 可编辑分组判断
  // NOTE: userInfo 没有时，说明是外部成员，不判断
  if (Array.isArray(group) && userInfo) {
    const isManager = group.includes('-1');
    const isOuterMember = group.includes('-2') && [1, 3].includes(userInfo.role);
    if (
      !isManager &&
      !isOuterMember &&
      !intersection(group, defaultTo([])(userInfo.group_ids))?.length
    )
      return false;
  }
  return true;
};

export const getParentDepartments = (
  departmentId: string,
  departmentNodes: OrgGroups.OrgDepartmentNodeInfo[],
) => {
  const departmentNodeMap: Record<string, OrgGroups.OrgDepartmentNodeInfo> = departmentNodes.reduce(
    (r, i) => ({
      ...r,
      [i.id]: i,
    }),
    {},
  );

  function getParentDepartment(id?: string, result: string[] = []): string[] {
    if (!id) return result;

    const parentId = departmentNodeMap[id]?.parentId!;

    if (!parentId) return result;

    return getParentDepartment(parentId, [...result, parentId]);
  }

  return getParentDepartment(departmentId);
};

export const getDepartmentByUser = (
  userInfo: OrgUser.OrgUserItem,
  departmentNodes: OrgGroups.OrgDepartmentNodeInfo[],
  _level: number = 0,
): OrgGroups.OrgDepartmentNodeInfo[] => {
  const level = _level;
  const departmentNodeMap: Record<string, OrgGroups.OrgDepartmentNodeInfo> = departmentNodes.reduce(
    (r, i) => ({
      ...r,
      [i.id]: i,
    }),
    {},
  );

  const ids: string[] = [];
  departmentNodes.forEach((n) => {
    if (userInfo && n.user.includes(userInfo.account_id)) {
      ids.push(n.id);
    }
  });

  function getParentDepartment(id?: string, level = 0): string | null {
    if (!id) return null;
    if (level <= 0) return id;
    const parentId = departmentNodeMap[id].parentId;

    return getParentDepartment(parentId, level - 1);
  }

  const parentIds = ids
    .map((id) => getParentDepartment(id, level))
    .filter((i) => !isNil(i)) as string[];

  return parentIds.map((id) => departmentNodeMap[id]);
};

export const getUserByDepartment = <T extends OrgUser.OrgUserItem>(
  departmentId: string,
  userMap: Record<string, T>,
): T[] => {
  const result: T[] = [];

  values(userMap).forEach((user) => {
    if (user.org_struct_ids?.includes(departmentId)) {
      result.push(user);
    }
  });

  return result;
};

export const getSupervisorByDepartment = (
  departmentId: string,
  departmentNodes: OrgGroups.OrgDepartmentNodeInfo[],
  userMap: Record<string, OrgUser.OrgUserItem>,
): OrgUser.OrgUserItem[] => {
  const result: OrgUser.OrgUserItem[] = [];

  const departmentNode = departmentNodes.find((d) => d.id === departmentId);

  departmentNode?.supervisor?.forEach((userId) => {
    result.push(userMap[userId]);
  });

  return result;
};

/**
 * 返回组织架构数据及其当前部门的人员
 */
export const getDepartmentNodes = (
  groups: OrgGroups.OrgGroupsItem[],
  userMap: Record<string, OrgUser.OrgUserItem>,
): OrgGroups.OrgDepartmentNodeInfo[] => {
  const userList = values(userMap)
    .filter((i) => i.org_struct_ids)
    .map((i) =>
      i.org_struct_ids.map((id) => ({
        userId: i.account_id,
        groudId: id,
      })),
    )
    .flat();

  const userListMapByDepartment = groupBy((i: any) => i.groudId)(userList);

  return groups
    .filter((g) => g.org_struct === 1)
    .map((g) => {
      return {
        parentId: g.parent_id,
        id: g.group_id,
        pathName: g.group_name,
        name: last(g.group_name.split('/')) as string,
        supervisor: g.manager || [],
        user: userListMapByDepartment[g.group_id]?.map((i) => i.userId) || [],
      };
    });
};

/**
 * 返回组织架构数据及其当前部门和所有子部门的人员
 */
export const getDepartmentNodesAndAllUsers = (
  groups: OrgGroups.OrgGroupsItem[],
  userMap: Record<string, OrgUser.OrgUserItem>,
): OrgGroups.OrgDepartmentNodeInfo[] => {
  const userList = values(userMap)
    .filter((i) => i.org_struct_ids_all)
    .map((i) =>
      i.org_struct_ids_all.map((id) => ({
        userId: i.account_id,
        groudId: id,
      })),
    )
    .flat();

  const userListMapByDepartment = groupBy((i: any) => i.groudId)(userList);

  return groups
    .filter((g) => g.org_struct === 1)
    .map((g) => {
      return {
        parentId: g.parent_id,
        id: g.group_id,
        pathName: g.group_name,
        name: last(g.group_name.split('/')) as string,
        supervisor: g.manager || [],
        user: userListMapByDepartment[g.group_id]?.map((i) => i.userId) || [],
      };
    });
};
